import React, { useState, useEffect, useRef } from "react";
import useAxiosPrivate from '../../hooks/useAxiosPrivate'
import { useLocation, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { BsThreeDotsVertical } from "react-icons/bs";
import RecommendationWrapper from "./component/Dashboard/RecommendationWrapper";
import Fuse from 'fuse.js';
import { DeleteModal } from "../Table/component/Table";

const ResearchCard = ({ group, onEdit, onDelete }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null)

  // Close menu if clicked outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative border bg-white shadow-lg dark:bg-darkbg4 rounded-xl shadow-sm mb-6 py-6 px-8 bg-white">
      <div className="absolute right-3" ref={menuRef}>
        <button
          onClick={() => setMenuOpen((prev) => !prev)}
          className="rounded-full p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <BsThreeDotsVertical className="h-4 w-4 text-gray-600 dark:text-white" />
        </button>

        {/* Dropdown menu */}
        {menuOpen && (
          <div className="absolute right-0 mt-1 w-28 bg-white border rounded-md shadow-lg z-20">
            <button
              onClick={() => {
                setMenuOpen(false);
                onEdit(group);
              }}
              className="block w-full rounded-t-md text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              Edit
            </button>
            <button
              onClick={() => {
                setMenuOpen(false);
                onDelete(group.id);
              }}
              className="block w-full rounded-b-md text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
            >
              Delete
            </button>
          </div>
        )}

      </div>
      <div className="flex items-start gap-4">
        <div className="flex-1">
          <h2 className="text-xl font-semibold">{group.title}</h2>
          <div className="flex gap-2 mt-1">
            {group.tags.map((tag) => (
              <span
                key={tag}
                className="bg-gray-200 text-gray-700 text-xs px-2 py-1 rounded"
              >
                {tag}
              </span>
            ))}
          </div>
          <h3 className="text-sm font-semibold mt-3">Summary</h3>
          <p className="text-sm mt-1">{group.summary}</p>

          {showDetails && group.details && (
            <div className="mt-3 text-sm">
              <p>
                <strong>Maturity:</strong> {group.details.maturity} <br />
                <strong>Emission Reduction:</strong> {group.details.emissionReduction}% <br />
                <strong>Focus area:</strong> {group.details.focus} <br />
                <strong>Supervisor:</strong> {group.details.supervisor} <br />
                <strong>University:</strong> {group.details.university} <br />
                <strong>Latest review:</strong> {new Date(group.updatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            </div>
          )}

          <div className="mt-4 flex gap-2">
            <a
              href={group.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Source
            </a>
            <button
              className="text-blue-600 dark:text-blue-400 underline text-sm"
              onClick={() => setShowDetails(!showDetails)}
            >
              {showDetails ? "Show less" : "Show more"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Research = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const axiosPrivate = useAxiosPrivate()
  const [researchGroups, setResearchGroups] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([])
  const [showContentModal, setShowContentModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [modalMode, setModalMode] = useState(null) // 'add' or 'edit'
  const [rowToDelete, setRowToDelete] = useState(null)
  const [editableData, setEditableData] = useState({
    id: "",
    title: "",
    tags: [],
    summary: "",
    maturity: "",
    emissionReduction: "",
    focus: "",
    supervisor: "",
    university: "",
    sourceUrl: ""
  })

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchResearch = async () => {
      setIsLoading(true);
      try {
        const response = await axiosPrivate.get('research/read-record', {
          signal: controller.signal,
        });

        if (isMounted) {
          setResearchGroups(response.data)
          setSearchResults(response.data)
        }
      } catch (err) {
        if (axios.isCancel(err)) {
          // Log for debugging purposes 
          // // console.log('Request cancelled:', err.message);
        } else {
          console.error('Failed to fetch research projects:', err);
          if (err.response && err.response.status === 403) {
            navigate('/sign-in', { state: { from: location }, replace: true });
          }
        }
      }
      setIsLoading(false);
    };

    fetchResearch();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [navigate, location, axiosPrivate]);

  // Filter data based on search term
  useEffect(() => {
    // Initialize Fuse with the list of users and the options for fuzzy search
    const fuse = new Fuse(researchGroups, {
      keys: ['title', 'tag', 'summary'],
      includeScore: true,
    });

    if (searchTerm.trim().length > 0) {
      const results = fuse.search(searchTerm).map((result) => result.item);
      setSearchResults(results);
    } else {
      setSearchResults(researchGroups); // Reset to original data if search term is cleared
    }
  }, [searchTerm, researchGroups]);


  const handleAdd = () => {
    setEditableData({
      title: "",
      tags: [],
      summary: "",
      maturity: "",
      emissionReduction: "",
      focus: "",
      supervisor: "",
      university: "",
      sourceUrl: ""
    })
    setModalMode('add')
    setShowContentModal(true)
  };
  const handleEdit = (group) => {
    setEditableData({
      ...group,        // spread top-level fields
      ...group.details // spread details into top-level
    })
    setModalMode('edit')
    setShowContentModal(true)

  };

  const handleDelete = (id) => {
    setShowDeleteModal(true)
    setRowToDelete(id)

  };

  const confirmDelete = async () => {
    if (rowToDelete !== null) {
      let isMounted = true
      const controller = new AbortController()

      try {
        await axiosPrivate.delete(`/research/delete-record/${rowToDelete}`, {
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
        })

        if (isMounted) {
          // Update the state to reflect the deletion
          setResearchGroups((prevData) =>
            prevData.filter((row) => row.id !== rowToDelete),
          )
          setSearchResults((prevData) =>
            prevData.filter((row) => row.id !== rowToDelete),
          )
          setRowToDelete(null)
        }
      } catch (err) {
        if (!axios.isCancel(err)) {
          console.error('Deletion failed:', err)
          if (err.response) {
            // Redirect on 403 Forbidden or show an error message
            if (err.response.status === 403) {
              navigate('/sign-in', {
                state: { from: location },
                replace: true,
              })
            } else {
              alert(
                `Error: ${err.response.data.message || 'An error occurred.'}`,
              )
            }
          }
        }
      } finally {
        if (isMounted) {
          setShowDeleteModal(false) // Close the modal upon completion or error
        }
      }

      // Cleanup function to prevent state updates if the component unmounts
      return () => {
        isMounted = false
        controller.abort()
      }
    } else {
      // If rowToDelete is null, just close the modal
      setShowDeleteModal(false)
    }
  }

  const handleAddNewSubmit = async (event) => {
    event.preventDefault()

    const endpoint =
      modalMode === 'edit'
        ? `/research/edit-record/${editableData.id}`
        : '/research/create-record'
    const method = modalMode === 'edit' ? 'patch' : 'post'
    // Prepare the data payload
    const payload = {
      title: editableData.title,
      tags: typeof(editableData.tags) === "object" ? editableData.tags : editableData.tags.split(',').map(s => s.trim()),
      summary: editableData.summary,
      maturity: editableData.maturity,
      emissionReduction: editableData.emissionReduction,
      focus: editableData.focus,
      supervisor: editableData.supervisor,
      university: editableData.university,
      sourceUrl: editableData.sourceUrl

    }

    try {
      const response = await axiosPrivate[method](endpoint, payload, {
        headers: { 'Content-Type': 'application/json' },
      })

      // Update the state based on the modal mode
      if (modalMode === 'edit') {
        setResearchGroups((currentData) =>
          currentData.map((item) =>
            item.id === editableData.id ? { ...item, ...response.data } : item,
          ),
        )
      } else {
        setResearchGroups((currentData) => [...currentData, response.data])
      }

      // Reset form and close modal
      setEditableData({
        id: "",
        title: "",
        tags: [],
        summary: "",
        maturity: "",
        emissionReduction: "",
        focus: "",
        supervisor: "",
        university: "",
        sourceUrl: ""
      })
      setShowContentModal(false)
    } catch (err) {
      if (!axios.isCancel(err)) {
        console.error('Submission failed:', err)
        const errorMsg = err.response?.data.message || 'An error occurred.'
        if (err.response?.status === 403) {
          navigate('/sign-in', { state: { from: location }, replace: true })
        } else {
          alert(`Error: ${errorMsg}`)
        }
      }
    }
  }

  return (
    <RecommendationWrapper
      title="Research"
      onAdd={handleAdd}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      placeholder="Search ">
      {searchResults.map((group) => (
        <ResearchCard
          key={group.id}
          group={group}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      ))}

      {showDeleteModal && (
        <DeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={(row) => (row !== null ? confirmDelete() : setShowDeleteModal(false))}
        rowToDelete={rowToDelete}
      />
      )}

      {showContentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-md flex justify-center items-center p-4">
          <div className="bg-white dark:bg-gray-800 w-full max-w-lg mx-auto rounded shadow-lg overflow-hidden p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4">
              {modalMode === 'edit' ? 'Edit Data' : 'Add New Data'}
            </h2>
            <form onSubmit={handleAddNewSubmit}>
              {/* Title Input */}
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Title
                </label>
                <input
                  type="text"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                  value={editableData.title}
                  onChange={(e) =>
                    setEditableData({
                      ...editableData,
                      title: e.target.value,
                    })
                  }
                  required
                />
              </div>

              {/* Tags Input */}
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Tags
                </label>
                <input
                  type="text"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                  placeholder='Use this format Alpha,Beta,Charlie'
                  value={editableData.tags}
                  onChange={(e) =>
                    setEditableData({
                      ...editableData,
                      tags: e.target.value,
                    })
                  }
                  required
                />
              </div>

              {/* Summary Input */}
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Summary
                </label>
                <input
                  type="text"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                  value={editableData.summary}
                  onChange={(e) =>
                    setEditableData({
                      ...editableData,
                      summary: e.target.value,
                    })
                  }
                  required
                />
              </div>

              {/* Maturity Input */}
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Maturity
                </label>
                <select
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                  value={editableData.maturity}
                  onChange={(e) =>
                    setEditableData({
                      ...editableData,
                      maturity: e.target.value,
                    })
                  }
                  required
                >
                  <option value="">Select Level</option>
                  <option value="Prototype">Prototype</option>
                </select>
              </div>

              {/* Emission Reduction Input */}
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Emission Reduction (%)
                </label>
                <input
                  type="number"
                  onWheel={(e) => e.target.blur()}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                  value={editableData.emissionReduction}
                  onChange={(e) =>
                    setEditableData({
                      ...editableData,
                      emissionReduction: e.target.value,
                    })
                  }
                  required
                />
              </div>

              {/* Focus Input */}
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Focus
                </label>
                <input
                  type="text"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                  value={editableData.focus}
                  onChange={(e) =>
                    setEditableData({
                      ...editableData,
                      focus: e.target.value,
                    })
                  }
                  required
                />
              </div>

              {/* Supervisor Input */}
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Supervisor
                </label>
                <input
                  type="text"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                  value={editableData.supervisor}
                  onChange={(e) =>
                    setEditableData({
                      ...editableData,
                      supervisor: e.target.value,
                    })
                  }
                  required
                />
              </div>

              {/* University Input */}
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  University
                </label>
                <input
                  type="text"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                  value={editableData.university}
                  onChange={(e) =>
                    setEditableData({
                      ...editableData,
                      university: e.target.value,
                    })
                  }
                  required
                />
              </div>

              {/* University Input */}
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Source URL
                </label>
                <input
                  type="text"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                  value={editableData.sourceUrl}
                  onChange={(e) =>
                    setEditableData({
                      ...editableData,
                      sourceUrl: e.target.value,
                    })
                  }
                  required
                />
              </div>

              <div className="flex justify-end mt-4">
                <button
                  type="button"
                  onClick={() => setShowContentModal(false)}
                  className="bg-gray-300 dark:bg-gray-600 dark:text-gray-200 px-4 py-2 rounded mr-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-500 text-white px-4 py-2 rounded"
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </RecommendationWrapper>
  );
};

export default Research;
